import React from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import usePaymentModes from "src/hooks/usePaymentModes";
import { handleValidNumber2 } from "src/utils/formUtils/ValidNumber";
import { DrawerExchangeFormValue } from "src/models/Drawer.model";
import { useNavigate } from "react-router-dom";

type Props = {
  formikProps: FormikProps<DrawerExchangeFormValue>;
};

const DrawerExchangeForm = ({ formikProps }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { paymentMode: paymentModes } = usePaymentModes();
  const navigate = useNavigate();
  // Get payment modes
  const getPaymentModes = (type: "PHYSICAL" | "VIRTUAL") => {
    return (
      paymentModes
        ?.filter((pm: any) => pm?.type === type)
        .map((pm: any) => ({
          label: pm?.name,
          value: pm?.value,
          type: pm?.type,
        })) || []
    );
  };

  const virtualPaymentModes = getPaymentModes("VIRTUAL");

  const handleQuantityChangeOut = (index: number, value: string) => {
    const quantity = Number(value);
    const available = Number(values.outflow[index].availableQuantity || 0);

    if (quantity >= 0 && quantity <= available) {
      setFieldValue(`outflow[${index}].quantity`, value);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">Payment Exchange</div>
        <button
          type="button"
          className="hover:bg-hover"
          onClick={() => navigate("/drawer")}
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inflow Section */}
        <div>
          <div className="border rounded-md p-1">
            <p className="text-xl font-medium mb-2">Payment Deposit</p>
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="border px-4 py-2">Note Value</th>
                  <th className="border px-4 py-2">Quantity</th>
                  <th className="border px-4 py-2">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <FieldArray name="inflow">
                  {() =>
                    values.inflow.filter((item)=>item.type==="PHYSICAL").map((note, index) => {
                      // const qty = Number(note.quantity || 0);

                      return (
                        <tr key={index}>
                          <td className="border px-4 py-2">{note.name} X</td>
                          <td className="border px-4 py-2">
                            <ATMTextField
                              name={`inflow[${index}].quantity`}
                              value={values.inflow[index].quantity}
                              onChange={(e) =>
                                handleValidNumber2(e) &&
                                setFieldValue(
                                  `inflow[${index}].quantity`,
                                  e.target.value
                                )
                              }
                              placeholder="Enter quantity"
                              onBlur={handleBlur}
                            />
                          </td>
                          <td className="border px-4 py-2">
                            {note.quantity * note.value}
                          </td>
                        </tr>
                      );
                    })
                  }
                </FieldArray>
              </tbody>
            </table>
          </div>

          <div className="border rounded-md p-1">
            <p className="text-xl font-medium my-2">Payment Deposit (Online)</p>
            <table className="w-full border text-left">
              <thead>
                <tr className="bg-gray-100 text-sm">
                  <th className="p-2">Payment In</th>
                  <th className="p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {virtualPaymentModes.map((mode, i) => {
                  const item = values.inflow.find(
                    (item) => item.name === mode.label
                  ) || { value: 0 }; // Changed to use value

                  return (
                    <tr key={i} className="border-t">
                      <td className="p-2">{mode.label}</td>
                      <td className="p-2">
                        <ATMTextField
                          name={`inflow_${mode.value}`}
                          value={item.value.toString()} // Using value instead of quantity
                          onChange={(e) => {
                            const amount = e.target.value;
                            if (
                              handleValidNumber2({
                                target: { value: amount },
                              } as React.ChangeEvent<HTMLInputElement>)
                            ) {
                              const newInflow = values.inflow.filter(
                                (item) => item.name !== mode.label
                              );

                              if (amount) {
                                newInflow.push({
                                  name: mode.label,
                                  value: Number(amount), // Storing amount in value field
                                  quantity: 1, // Keeping quantity as 1 for virtual payments
                                  type: mode.type,
                                });
                              }

                              setFieldValue("inflow", newInflow);
                            }
                          }}
                          onBlur={handleBlur}
                          label=""
                          placeholder="Amount"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Outflow Section */}
        <div className="border rounded-md p-1">
          <p className="text-xl font-medium mb-2">Payment Out</p>
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="border px-4 py-2">Note Value</th>
                <th className="border px-4 py-2">Quantity</th>
                <th className="border px-4 py-2">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <FieldArray name="outflow">
                {() =>
                  values.outflow.map((note, index) => {
                    const availableQty = Number(note.availableQuantity || 0);
                    const isDisabled = availableQty === 0;

                    return (
                      <tr key={index}>
                        <td className="border px-4 py-2">{note.value} X</td>
                        <td className="border px-4 py-2">
                          <ATMTextField
                            name={`outflow[${index}].quantity`}
                            disabled={isDisabled}
                            placeholder="Qty"
                            value={note.quantity}
                            onChange={(e) =>
                              handleQuantityChangeOut(index, e.target.value)
                            }
                          />
                          {isDisabled ? (
                            <span className="text-red-400 text-sm">
                              not available
                            </span>
                          ) : (
                            <span className="text-sm">
                              Total available quantity {availableQty}
                            </span>
                          )}
                        </td>
                        <td className="border px-4 py-2">
                          {note.quantity * note.value}
                        </td>
                      </tr>
                    );
                  })
                }
              </FieldArray>
            </tbody>
          </table>
        </div>

        {/* Reason */}
        <div>
          <ATMTextArea
            name="reason"
            value={values.reason}
            onChange={(e) => setFieldValue("reason", e)}
            label="Reason"
          />
        </div>

        {/* Submit */}
        <div>
          <ATMLoadingButton isLoading={isSubmitting} type="submit">
            Save
          </ATMLoadingButton>
        </div>
      </div>
    </div>
  );
};

export default DrawerExchangeForm;
